import BetaParticipation from "./BetaParticipation";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Form } from "react-bootstrap";
import { reviewApi, dateLabel, topicLabel } from "./api";
import { countries } from "../Questions/countries";
import "./reviews.css";
import AttorneyQualifications, {
  emptyQualifications,
} from "./AttorneyQualifications";
import { usePortal } from "../Portal/PortalContext";
const profileStatuses: Record<string, string> = {
  approved: "Approved",
  pending: "Awaiting approval",
  suspended: "Suspended",
  declined: "Declined",
};
function coverageLocation(j: any) {
  if (!j || j.level === "unsure") return "Jurisdiction to clarify";
  const country =
    countries.find(c => c.code === j.countryCode)?.name || j.countryCode;
  return [
    j.region,
    country,
    j.level === "federal"
      ? "Federal"
      : j.level === "country"
        ? "Country-wide"
        : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
export default function AttorneyProfile() {
  const { access, refresh } = usePortal();
  const [form, setForm] = useState<any>({
      ...emptyQualifications(),
      available: true,
      version: 0,
    }),
    [profile, setProfile] = useState<any>(null),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false);
  useEffect(() => {
    const c = new AbortController();
    reviewApi("/me", "GET", undefined, c.signal)
      .then(({ profile: p }) => {
        setProfile(p);
        if (p)
          setForm({
            displayName: p.display_name,
            bio: p.bio,
            experienceYears: p.experience_years,
            languages: p.languages,
            admissions: p.admissions,
            coverage: p.coverage,
            available: p.available,
            ...p.pending_profile,
            version: p.version,
          });
      })
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, []);
  const change = (key: string, value: any) => {
    setSaved(false);
    setForm((f: any) => ({ ...f, [key]: value }));
  };
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const { profile: p } = await reviewApi("/profile", "PUT", form);
      setProfile(p);
      setForm((f: any) => ({ ...f, version: p.version }));
      setSaved(true);
      refresh();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (loading)
    return (
      <main className="reviewed-documents">Loading attorney profile…</main>
    );
  return (
    <main className="reviewed-documents review-page">
      {access?.accountType === "customer" ? (
        <Link to="/questions">← Your workspace</Link>
      ) : access?.attorney ? (
        <Link to="/attorney/reviews">← Attorney portal</Link>
      ) : null}
      <header className="reviewed-documents-heading mt-4">
        <div>
          <p className="reviewed-eyebrow">
            {profile?.status === "approved"
              ? "PROFESSIONAL PROFILE"
              : "ATTORNEY APPLICATION"}
          </p>
          <h1>Your professional profile</h1>
          <p>
            {profile?.status === "approved"
              ? "Keep your qualifications, practice coverage, and availability current so assignments fit the work you can take on."
              : "Tell us which questions you are qualified to review. An admin will check your profile and coverage before you receive assignments."}
          </p>
        </div>
        {profile && (
          <span className="review-badge">
            {profileStatuses[profile.status] || "Status unavailable"}
          </span>
        )}
      </header>
      {access?.targetedBeta && <BetaParticipation />}
      {error && <Alert variant="danger">{error}</Alert>}
      {profile && profile.status !== "approved" && (
        <Alert variant={profile.status === "suspended" ? "warning" : "info"}>
          {profile.status === "pending"
            ? "Your application is awaiting admin approval. Assigned reviews become available once you are approved."
            : profile.status === "suspended"
              ? "Your attorney access is suspended. An admin must restore approval before you can review client submissions."
              : "Your application was declined. You can update your profile and submit it for another review."}
        </Alert>
      )}
      {profile?.pending_profile && (
        <Alert variant="info">
          Your changes are awaiting approval.{" "}
          {profile.status === "approved" ? (
            <>
              Your existing approved profile and coverage remain active, so you
              can continue assigned work. New coverage becomes available only
              after approval.
            </>
          ) : (
            <>Attorney access requires an approved profile.</>
          )}
        </Alert>
      )}
      {saved && (
        <Alert variant="success">
          Profile saved.{" "}
          {!profile?.pending_profile && profile?.status === "pending"
            ? "An admin must approve your application or changes before you can review questions."
            : ""}
        </Alert>
      )}
      {profile?.status === "approved" && (
        <section
          className="review-section"
          aria-labelledby="approved-coverage-heading"
        >
          <h2 id="approved-coverage-heading">
            Your approved practice coverage
          </h2>
          <p>
            You can receive assignments in these practice areas and locations.
          </p>
          <ul>
            {profile.coverage.map((c: any, i: number) => (
              <li key={i}>
                {topicLabel(c.topicKey)} · {coverageLocation(c.jurisdiction)}
              </li>
            ))}
          </ul>
          <p className="mb-0">
            {profile.available
              ? "Available for new assignments."
              : "Not accepting new assignments. Existing reviews remain available."}
          </p>
        </section>
      )}
      <Form onSubmit={save}>
        <fieldset disabled={busy}>
          <AttorneyQualifications
            form={form}
            onChange={change}
            showAvailability
          />
          <Alert variant="info">
            {profile?.status === "approved" ? (
              <>
                Changes to professional details or coverage are reviewed
                separately. Your existing approved qualifications remain active.
                Changing availability alone does not remove an existing
                approval.
              </>
            ) : (
              <>
                An admin must approve your qualifications and practice coverage
                before you can receive review assignments.
              </>
            )}
          </Alert>
          <Button type="submit" disabled={busy}>
            {busy
              ? "Saving…"
              : profile
                ? "Save profile"
                : "Submit attorney application"}
          </Button>
        </fieldset>
      </Form>
    </main>
  );
}
export function AdminAttorneys() {
  const [rows, setRows] = useState<any[]>([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    reviewApi("/attorneys", "GET", undefined, c.signal)
      .then(d => setRows(d.attorneys))
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [revision]);
  return (
    <main className="reviewed-documents review-page">
      <Link to="/admin/reviews">← Review assignments</Link>
      <header className="reviewed-documents-heading mt-4">
        <div>
          <p className="reviewed-eyebrow">OPERATIONS</p>
          <h1>Attorney applications</h1>
          <p>
            Check each attorney’s qualifications and practice coverage before
            approving them for client assignments.
          </p>
        </div>
        <Button variant="light" onClick={() => setRevision(n => n + 1)}>
          Refresh
        </Button>
      </header>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <p>Loading applications…</p>
      ) : !rows.length ? (
        <section className="reviewed-empty">
          <h2>No attorney applications yet</h2>
          <p>
            Attorneys can sign in and apply through their professional profile.
          </p>
          <p>
            Attorneys can begin at the “For attorneys” link on the sign-in page
            using their own account.
          </p>
        </section>
      ) : (
        rows.map(a => (
          <AttorneyApproval
            key={a.user_id}
            attorney={a}
            onSaved={() => setRevision(n => n + 1)}
          />
        ))
      )}
    </main>
  );
}
function AttorneyApproval({
  attorney: a,
  onSaved,
}: {
  attorney: any;
  onSaved: () => void;
}) {
  const proposed = a.pending_profile;
  const display = proposed
    ? {
        ...a,
        display_name: proposed.displayName,
        bio: proposed.bio,
        experience_years: proposed.experienceYears,
        languages: proposed.languages,
        admissions: proposed.admissions,
        coverage: proposed.coverage,
      }
    : a;
  const [notes, setNotes] = useState(a.verification_notes || ""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function update(status: string) {
    setBusy(true);
    setError("");
    try {
      await reviewApi(`/attorneys/${a.user_id}`, "PATCH", {
        status,
        verificationNotes: notes,
        version: a.version,
      });
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <article className="review-section">
      <div className="review-card-top">
        <h2>{display.display_name}</h2>
        <span className="review-badge">
          {profileStatuses[a.status] || "Status unavailable"}
        </span>
      </div>
      <p>
        {a.email} · {display.experience_years} years (self-reported) ·{" "}
        {a.available ? "Available" : "Unavailable"}
      </p>
      <p>{display.bio}</p>
      <div className="review-stats">
        <div>
          <strong>{a.active_count}</strong>Active reviews
        </div>
        <div>
          <strong>{a.completed_count}</strong>Delivered reviews
        </div>
        <div>
          <strong>{a.needs_reply_count}</strong>Need a reply
        </div>
      </div>
      {proposed && (
        <Alert variant="info">
          <strong>Proposed profile changes</strong>
          <p>
            The details below are awaiting approval. Existing access remains
            approved.
          </p>
          <details>
            <summary>Currently approved profile</summary>
            <p>
              {a.display_name} · {a.experience_years} years · {a.bio}
            </p>
            <ul>
              {a.coverage.map((c: any, i: number) => (
                <li key={i}>
                  {topicLabel(c.topicKey)} · {coverageLocation(c.jurisdiction)}
                </li>
              ))}
            </ul>
          </details>
        </Alert>
      )}
      <h3 className="h6">Admissions</h3>
      {display.admissions.map((d: any, i: number) => (
        <p key={i}>
          {d.jurisdiction},{" "}
          {countries.find(c => c.code === d.country)?.name || d.country} ·{" "}
          {d.authority} · {d.licenseNumber || "No number provided"} ·{" "}
          {d.admittedOn} · {d.status === "active" ? "Active" : "Inactive"}
        </p>
      ))}
      <h3 className="h6">Coverage requested</h3>
      <ul>
        {display.coverage.map((c: any, i: number) => (
          <li key={i}>
            {topicLabel(c.topicKey)} · {coverageLocation(c.jurisdiction)}
          </li>
        ))}
      </ul>
      {a.reviewed_at && <small>Last reviewed {dateLabel(a.reviewed_at)}</small>}
      <Form.Group className="mt-3" controlId={`notes-${a.user_id}`}>
        <Form.Label>
          Internal qualification checks and decision notes
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          maxLength={4000}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </Form.Group>
      {error && (
        <Alert className="mt-3" variant="danger">
          {error}
        </Alert>
      )}
      <div className="review-header-actions mt-3">
        <Button
          disabled={busy || !notes.trim()}
          onClick={() => update("approved")}
        >
          {proposed ? "Approve changes" : "Approve"}
        </Button>
        <Button
          variant="outline-warning"
          disabled={busy || !notes.trim()}
          onClick={() => update("suspended")}
        >
          Suspend
        </Button>
        <Button
          variant="outline-danger"
          disabled={busy || !notes.trim()}
          onClick={() => update("declined")}
        >
          {proposed ? "Decline changes" : "Decline"}
        </Button>
      </div>
    </article>
  );
}
